import { collection, query, getDocs, orderBy, doc, getDoc, where, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../backend/firebaseConfig";

// Function to fetch all posts
export async function fetchAllPosts() {
  try {
    const postsCollection = collection(firestore, "Posts");
    const usersCollection = collection(firestore, "users");
    
    const q = query(postsCollection, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const posts = await Promise.all(
      querySnapshot.docs.map(async (postDoc) => {
        const postData = postDoc.data();
        
        // Get user profile data
        let userProfile = null;
        try {
          const userDocRef = doc(usersCollection, postData.userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userProfile = {
              profileImage: userData.profileImage || "/Images/default-profile.jpeg",
              fullname: userData.fullname,
              userType: userData.userType,
              email: userData.email
            };
          }
        } catch (userError) {
          console.error(`Error fetching user ${postData.userId}:`, userError);
        }
        
        return {
          id: postDoc.id,
          ...postData,
          userProfile
        };
      })
    );
    
    console.log("Fetched posts: ", posts);
    return posts;
  } catch (error) {
    console.error("Error fetching posts: ", error);
    throw error;
  }
}

// Function to fetch post requests for a specific driver (Admin view)
export async function fetchPostRequestsByDriverId(driverId) {
  try {
    const postRequestsCollection = collection(firestore, "PostRequests");
    const usersCollection = collection(firestore, "users");
    const postsCollection = collection(firestore, "Posts");
    
    const q = query(
      postRequestsCollection, 
      where("riderId", "==", driverId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    
    const postRequests = await Promise.all(
      querySnapshot.docs.map(async (requestDoc) => {
        const requestData = requestDoc.data();
        
        // Get poster profile data
        let posterProfile = null;
        try {
          const posterDocRef = doc(usersCollection, requestData.posterId);
          const posterDoc = await getDoc(posterDocRef);
          
          if (posterDoc.exists()) {
            const posterData = posterDoc.data();
            posterProfile = {
              profileImage: posterData.profileImage || "/Images/default-profile.jpeg",
              fullname: posterData.fullname,
              userType: posterData.userType,
              email: posterData.email
            };
          }
        } catch (userError) {
          console.error(`Error fetching poster ${requestData.posterId}:`, userError);
        }
        
        // Get post details
        let postDetails = null;
        try {
          const postDocRef = doc(postsCollection, requestData.postId);
          const postDoc = await getDoc(postDocRef);
          
          if (postDoc.exists()) {
            postDetails = {
              id: postDoc.id,
              ...postDoc.data()
            };
          }
        } catch (error) {
          console.error(`Error fetching post ${requestData.postId}:`, error);
        }
        
        return {
          id: requestDoc.id,
          ...requestData,
          posterProfile,
          postDetails
        };
      })
    );
    
    console.log("Fetched Post Requests: ", postRequests);
    return postRequests;
  } catch (error) {
    console.error("Error fetching post requests: ", error);
    throw error;
  }
}

// Function to fetch all post requests (Admin overview)
export async function fetchAllPostRequests() {
  try {
    const postRequestsCollection = collection(firestore, "PostRequests");
    const usersCollection = collection(firestore, "users");
    const postsCollection = collection(firestore, "Posts");
    
    const q = query(postRequestsCollection, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const postRequests = await Promise.all(
      querySnapshot.docs.map(async (requestDoc) => {
        const requestData = requestDoc.data();
        
        // Get rider (driver) profile data
        let riderProfile = null;
        try {
          const riderDocRef = doc(usersCollection, requestData.riderId);
          const riderDoc = await getDoc(riderDocRef);
          
          if (riderDoc.exists()) {
            const riderData = riderDoc.data();
            riderProfile = {
              profileImage: riderData.profileImage || "/Images/default-profile.jpeg",
              fullname: riderData.fullname,
              userType: riderData.userType,
              email: riderData.email
            };
          }
        } catch (error) {
          console.error(`Error fetching rider ${requestData.riderId}:`, error);
        }
        
        // Get poster profile data
        let posterProfile = null;
        try {
          const posterDocRef = doc(usersCollection, requestData.posterId);
          const posterDoc = await getDoc(posterDocRef);
          
          if (posterDoc.exists()) {
            const posterData = posterDoc.data();
            posterProfile = {
              profileImage: posterData.profileImage || "/Images/default-profile.jpeg",
              fullname: posterData.fullname,
              userType: posterData.userType,
              email: posterData.email
            };
          }
        } catch (error) {
          console.error(`Error fetching poster ${requestData.posterId}:`, error);
        }
        
        // Get post details
        let postDetails = null;
        try {
          const postDocRef = doc(postsCollection, requestData.postId);
          const postDoc = await getDoc(postDocRef);
          
          if (postDoc.exists()) {
            postDetails = {
              id: postDoc.id,
              ...postDoc.data()
            };
          }
        } catch (error) {
          console.error(`Error fetching post ${requestData.postId}:`, error);
        }
        
        return {
          id: requestDoc.id,
          ...requestData,
          riderProfile,
          posterProfile,
          postDetails
        };
      })
    );
    
    console.log("Fetched All Post Requests: ", postRequests);
    return postRequests;
  } catch (error) {
    console.error("Error fetching all post requests: ", error);
    throw error;
  }
}

// Function to update post request status (Admin action)
export async function updatePostRequestStatus(requestId, newStatus) {
  try {
    const postRequestsCollection = collection(firestore, "PostRequests");
    const requestDocRef = doc(postRequestsCollection, requestId);
    
    await updateDoc(requestDocRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Post request ${requestId} status updated to ${newStatus}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating post request status: ", error);
    throw error;
  }
}

// Function to delete post request (Admin action)
export async function deletePostRequest(requestId) {
  try {
    const postRequestsCollection = collection(firestore, "PostRequests");
    const requestDocRef = doc(postRequestsCollection, requestId);
    
    await deleteDoc(requestDocRef);
    
    console.log(`Post request ${requestId} deleted`);
    return { success: true };
  } catch (error) {
    console.error("Error deleting post request: ", error);
    throw error;
  }
}

// Function to update post (Admin action)
export async function updatePost(postId, updateData) {
  try {
    const postsCollection = collection(firestore, "Posts");
    const postDocRef = doc(postsCollection, postId);
    
    await updateDoc(postDocRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Post ${postId} updated successfully`);
    return { success: true };
  } catch (error) {
    console.error("Error updating post: ", error);
    throw error;
  }
}

// Function to delete post (Admin action)
export async function deletePost(postId) {
  try {
    const postsCollection = collection(firestore, "Posts");
    const postRequestsCollection = collection(firestore, "PostRequests");
    
    // Delete the post
    const postDocRef = doc(postsCollection, postId);
    await deleteDoc(postDocRef);
    
    // Delete all associated post requests
    const requestsQuery = query(postRequestsCollection, where("postId", "==", postId));
    const requestsSnapshot = await getDocs(requestsQuery);
    
    const deletePromises = requestsSnapshot.docs.map(requestDoc => 
      deleteDoc(doc(postRequestsCollection, requestDoc.id))
    );
    
    await Promise.all(deletePromises);
    
    console.log(`Post ${postId} and associated requests deleted`);
    return { success: true };
  } catch (error) {
    console.error("Error deleting post: ", error);
    throw error;
  }
}

// Function to get post statistics (Admin dashboard)
export async function getPostStatistics() {
  try {
    const postsCollection = collection(firestore, "Posts");
    const postRequestsCollection = collection(firestore, "PostRequests");
    
    // Get all posts
    const postsSnapshot = await getDocs(postsCollection);
    const totalPosts = postsSnapshot.size;
    
    // Get all post requests
    const requestsSnapshot = await getDocs(postRequestsCollection);
    const totalRequests = requestsSnapshot.size;
    
    // Count requests by status
    const requestsByStatus = {};
    requestsSnapshot.docs.forEach(doc => {
      const status = doc.data().status || 'unknown';
      requestsByStatus[status] = (requestsByStatus[status] || 0) + 1;
    });
    
    // Count posts by user type
    const usersCollection = collection(firestore, "users");
    const postsByUserType = {};
    
    await Promise.all(
      postsSnapshot.docs.map(async (postDoc) => {
        const postData = postDoc.data();
        try {
          const userDoc = await getDoc(doc(usersCollection, postData.userId));
          if (userDoc.exists()) {
            const userType = userDoc.data().userType || 'unknown';
            postsByUserType[userType] = (postsByUserType[userType] || 0) + 1;
          }
        } catch (error) {
          console.error('Error fetching user type:', error);
        }
      })
    );
    
    return {
      totalPosts,
      totalRequests,
      requestsByStatus,
      postsByUserType
    };
  } catch (error) {
    console.error("Error getting post statistics: ", error);
    throw error;
  }
}

// Function to fetch posts by user ID (Admin view)
export async function fetchPostsByUserId(userId) {
  try {
    const postsCollection = collection(firestore, "Posts");
    const usersCollection = collection(firestore, "users");
    
    const q = query(
      postsCollection, 
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    
    const posts = await Promise.all(
      querySnapshot.docs.map(async (postDoc) => {
        const postData = postDoc.data();
        
        // Get user profile data
        let userProfile = null;
        try {
          const userDocRef = doc(usersCollection, postData.userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userProfile = {
              profileImage: userData.profileImage || "/Images/default-profile.jpeg",
              fullname: userData.fullname,
              userType: userData.userType,
              email: userData.email
            };
          }
        } catch (userError) {
          console.error(`Error fetching user ${postData.userId}:`, userError);
        }
        
        return {
          id: postDoc.id,
          ...postData,
          userProfile
        };
      })
    );
    
    console.log("Fetched Posts by User: ", posts);
    return posts;
  } catch (error) {
    console.error("Error fetching posts by user: ", error);
    throw error;
  }
}

// Function to bulk update post requests (Admin action)
export async function bulkUpdatePostRequests(requestIds, newStatus) {
  try {
    const postRequestsCollection = collection(firestore, "PostRequests");
    
    const updatePromises = requestIds.map(requestId => {
      const requestDocRef = doc(postRequestsCollection, requestId);
      return updateDoc(requestDocRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    
    console.log(`${requestIds.length} post requests updated to ${newStatus}`);
    return { success: true, updatedCount: requestIds.length };
  } catch (error) {
    console.error("Error bulk updating post requests: ", error);
    throw error;
  }
}